import type { Band } from "./types";
import { ANS_MAX, centreFor } from "./constants";
import { ri, rr, random } from "./rng";

/**
 * Bandas 1-14 escritas a mano, 15+ compuestas proceduralmente. No hay ultima
 * banda: el nivel controla cuantos terminos tiene la expresion, que tan grandes
 * son los operandos y cuantos digitos lleva la respuesta.
 *
 * Contrato de todo generador: operandos enteros, respuesta entera de 2 a 4
 * digitos, texto sin punto decimal. Si no puede cumplirlo, devuelve null y el
 * llamador reintenta.
 */

const MINUS = "\u2212"; // signo menos tipografico, no guion
const TIMES = "\u00D7";
const DIV = "\u00F7";
const SQ = "\u00B2";

export const AUTHORED: Band[] = [
  {
    n: 1,
    label: "Suma sin llevar",
    fall: 1.0,
    gen: () => {
      // construida digito por digito: llevar es imposible por construccion
      const a = rr(1, 4) * 10 + ri(5);
      const b = rr(1, 4) * 10 + ri(5);
      return { txt: `${a} + ${b}`, ans: a + b };
    },
  },
  {
    n: 2,
    label: "Suma llevando",
    fall: 1.15,
    gen: () => {
      const a = rr(14, 58);
      const b = rr(14, 41);
      if (a + b > 99 || (a % 10) + (b % 10) < 10) return null;
      return { txt: `${a} + ${b}`, ans: a + b };
    },
  },
  {
    n: 3,
    label: "Resta sin prestar",
    fall: 1.15,
    gen: () => {
      const a = rr(45, 98);
      const b = rr(11, 44);
      if (a % 10 < b % 10 || a - b < 10) return null;
      return { txt: `${a} ${MINUS} ${b}`, ans: a - b };
    },
  },
  {
    n: 4,
    label: "Resta prestando",
    fall: 1.3,
    gen: () => {
      const a = rr(45, 96);
      const b = rr(12, 43);
      if (a % 10 >= b % 10 || a - b < 10) return null;
      return { txt: `${a} ${MINUS} ${b}`, ans: a - b };
    },
  },
  {
    n: 5,
    label: "Multiplicacion chica",
    fall: 1.3,
    gen: () => {
      const a = rr(2, 5);
      const b = rr(3, 7);
      if (a * b < 10) return null;
      return { txt: `${a} ${TIMES} ${b}`, ans: a * b };
    },
  },
  {
    n: 6,
    label: "Tablas de multiplicar",
    fall: 1.55,
    gen: () => {
      const a = rr(6, 9);
      const b = rr(6, 9);
      return { txt: `${a} ${TIMES} ${b}`, ans: a * b };
    },
  },
  {
    n: 7,
    label: "Division exacta",
    fall: 1.7,
    gen: () => {
      const b = rr(3, 9);
      const q = rr(11, 40);
      return { txt: `${q * b} ${DIV} ${b}`, ans: q };
    },
  },
  {
    n: 8,
    label: "Suma de tres digitos",
    fall: 1.75,
    gen: () => {
      const a = rr(105, 480);
      const b = rr(112, 430);
      if (a + b > 999) return null;
      return { txt: `${a} + ${b}`, ans: a + b };
    },
  },
  {
    n: 9,
    label: `Dos digitos ${TIMES} uno`,
    fall: 1.9,
    gen: () => {
      const a = rr(12, 39);
      const b = rr(3, 9);
      if (a * b > 999) return null;
      return { txt: `${a} ${TIMES} ${b}`, ans: a * b };
    },
  },
  {
    n: 10,
    label: "Cuadrados",
    fall: 2.0,
    gen: () => {
      const n = rr(11, 24);
      return { txt: `${n}${SQ}`, ans: n * n };
    },
  },
  {
    n: 11,
    label: "Resta de tres digitos",
    fall: 2.0,
    gen: () => {
      const a = rr(320, 960);
      const b = rr(105, 318);
      if (a - b < 100 || a % 10 >= b % 10) return null;
      return { txt: `${a} ${MINUS} ${b}`, ans: a - b };
    },
  },
  {
    n: 12,
    label: `Dos pasos: ${TIMES} y +`,
    fall: 2.15,
    gen: () => {
      const a = rr(4, 9);
      const b = rr(4, 9);
      const c = rr(7, 40);
      return { txt: `${a} ${TIMES} ${b} + ${c}`, ans: a * b + c };
    },
  },
  {
    n: 13,
    label: `Dos pasos: ${TIMES} y ${MINUS}`,
    fall: 2.3,
    gen: () => {
      const a = rr(5, 9);
      const b = rr(5, 9);
      const c = rr(11, 40);
      if (a * b - c < 10) return null;
      return { txt: `${a} ${TIMES} ${b} ${MINUS} ${c}`, ans: a * b - c };
    },
  },
  {
    n: 14,
    label: `Dos pasos: ${DIV} y +`,
    fall: 2.45,
    gen: () => {
      const b = rr(3, 9);
      const q = rr(6, 20);
      const c = rr(10, 60);
      return { txt: `${q * b} ${DIV} ${b} + ${c}`, ans: q + c };
    },
  },
];

export const NA = AUTHORED.length;

interface Term {
  v: number;
  txt: string;
}

/**
 * Un termino de una expresion compuesta. Los productos y cocientes se resuelven
 * dentro del termino, asi que la precedencia sale correcta sin parentesis.
 */
function makeTerm(lvl: number): Term {
  const roll = random();
  const grow = Math.min(30, lvl);
  if (roll < 0.3) {
    const a = rr(3, 9 + Math.floor(grow / 2));
    const b = rr(3, 9 + Math.floor(grow / 3));
    return { v: a * b, txt: `${a} ${TIMES} ${b}` };
  }
  if (roll < 0.5) {
    const b = rr(3, 9);
    const q = rr(6, 20 + grow * 2);
    return { v: q, txt: `${q * b} ${DIV} ${b}` };
  }
  if (roll < 0.62) {
    const n = rr(11, 20 + Math.floor(grow / 2));
    return { v: n * n, txt: `${n}${SQ}` };
  }
  const d = Math.min(4, 2 + Math.floor(lvl / 7));
  const v = rr(Math.pow(10, d - 1), Math.pow(10, d) - 1);
  return { v, txt: String(v) };
}

const cache = new Map<number, Band>();

export function band(n: number): Band {
  const key = Math.max(1, Math.floor(n));
  if (key <= NA) return AUTHORED[key - 1]!;
  const hit = cache.get(key);
  if (hit) return hit;

  const lvl = key - NA;
  const terms = Math.min(4, 2 + Math.floor((lvl - 1) / 6));
  const made: Band = {
    n: key,
    label: `Compuesta de ${terms} terminos \u00B7 N${lvl}`,
    fall: Math.min(7.0, 2.45 + lvl * 0.14),
    gen: () => {
      const first = makeTerm(lvl);
      let v = first.v;
      let txt = first.txt;
      for (let i = 1; i < terms; i++) {
        const u = makeTerm(lvl);
        if (random() < 0.62 || v - u.v < 10) {
          v += u.v;
          txt += ` + ${u.txt}`;
        } else {
          v -= u.v;
          txt += ` ${MINUS} ${u.txt}`;
        }
      }
      return v < 10 || v > ANS_MAX ? null : { txt, ans: v };
    },
  };
  cache.set(key, made);
  return made;
}

/**
 * Elige banda para una ola. Ventana deslizante centrada un poco por debajo del
 * centro actual: los problemas faciles siguen apareciendo mientras los dificiles
 * se filtran, asi nada llega como un muro.
 */
export function pickBand(wave: number): number {
  return Math.max(1, Math.round(centreFor(wave) + (random() * 2.6 - 1.9)));
}
